import { useCallback, useMemo } from 'react'

import type { VolumeEndpoint, VolumeStats } from '../../lib/api/admin.ts'
import { formatCompact, formatInt, pctOf } from '../../lib/format.ts'
import { agentHref } from '../../lib/logs/investigation.ts'
import { Link } from '../../lib/router.tsx'
import { usePolledResource } from '../../lib/usePolledResource.ts'
import { Panel } from '../primitives/Panel.tsx'
import { ErrorState, LoadingRow } from '../primitives/States.tsx'
import { TABLE, TD, TD_NUM, TH, TH_NUM, THEAD, TR } from '../primitives/table.ts'

/** Barra horizontal fina, proporcional ao maior da lista. */
function ShareBar({ value, max }: { value: number; max: number }) {
  const pct = max <= 0 ? 0 : Math.max(1, Math.round(pctOf(value, max)))
  return (
    <span aria-hidden className="block h-1 w-full bg-line">
      <span className="block h-1 bg-accent" style={{ width: `${pct}%` }} />
    </span>
  )
}

/**
 * Volume ACUMULADO por empresa (Black Ghost) — desde que o rollup existe, sem
 * janela de tempo (§4.8). Para volume por período, o painel de vazão e o total
 * do dia do stream respondem melhor. Clicar numa empresa abre o volume dela por
 * agente.
 */
export function VolumeStatsPanel({
  load,
  onOpenTenant,
  className = '',
}: {
  load: (signal: AbortSignal) => Promise<VolumeStats>
  onOpenTenant: (tenantId: string, tenantName: string | null) => void
  className?: string
}) {
  const fetcher = useCallback((signal: AbortSignal) => load(signal), [load])
  const { data, error, reload } = usePolledResource(fetcher, 60_000)

  const tenants = useMemo(
    () => [...(data?.tenants ?? [])].sort((a, b) => b.events_total - a.events_total),
    [data],
  )
  const max = tenants[0]?.events_total ?? 0

  return (
    <Panel
      title="Volume acumulado por empresa"
      className={className}
      flush
      meta={
        data !== null && (
          <span title={`${formatInt(data.events_total)} eventos`}>
            total <span className="tnum text-fg-secondary">{formatCompact(data.events_total)}</span>
          </span>
        )
      }
    >
      {error !== null && data === null ? (
        <div className="p-3">
          <ErrorState error={error} onRetry={reload} />
        </div>
      ) : data === null ? (
        <LoadingRow label="Somando o volume…" />
      ) : tenants.length === 0 ? (
        <p className="px-3 py-3 text-sm text-fg-muted">Nenhum volume registrado.</p>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Empresa
                </th>
                <th scope="col" className={TH_NUM}>
                  endpoints
                </th>
                <th scope="col" className={TH_NUM}>
                  eventos
                </th>
                <th scope="col" className={`${TH} w-1/4`}>
                  <span className="sr-only">participação</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((row) => (
                <tr key={row.tenant_id} className={TR}>
                  <th scope="row" className={`${TD} max-w-0 font-normal`}>
                    <button
                      type="button"
                      onClick={() => onOpenTenant(row.tenant_id, row.tenant_name)}
                      className="block max-w-full truncate text-left text-fg underline-offset-2 hover:underline"
                      title="Abrir o volume desta empresa por agente"
                    >
                      {row.tenant_name ?? row.tenant_id}
                    </button>
                  </th>
                  <td className={TD_NUM}>{formatInt(row.endpoints)}</td>
                  <td className={TD_NUM} title={formatInt(row.events_total)}>
                    {formatCompact(row.events_total)}
                  </td>
                  <td className={TD}>
                    <ShareBar value={row.events_total} max={max} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

/**
 * Ranking de agentes por volume acumulado. Usado no detalhe da empresa (Black
 * Ghost, `stats/volume?tenantId=`) e na visão geral do cliente (`/api/tenant/
 * stats/volume`, onde `endpoints` vem sempre preenchido).
 */
export function EndpointVolumeTable({
  endpoints,
  limit,
}: {
  endpoints: readonly VolumeEndpoint[]
  limit?: number
}) {
  // O servidor já ordena do maior para o menor; reordenar é só defesa.
  const sorted = useMemo(
    () => [...endpoints].sort((a, b) => b.events_total - a.events_total).slice(0, limit),
    [endpoints, limit],
  )
  const max = sorted[0]?.events_total ?? 0

  if (sorted.length === 0) {
    return <p className="px-3 py-3 text-sm text-fg-muted">Nenhum agente com volume registrado.</p>
  }

  return (
    <table className={TABLE}>
      <thead className={THEAD}>
        <tr>
          <th scope="col" className={TH}>
            Agente
          </th>
          <th scope="col" className={TH_NUM}>
            eventos
          </th>
          <th scope="col" className={TH}>
            Ativo em
          </th>
          <th scope="col" className={`${TH} w-1/4`}>
            <span className="sr-only">participação</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <tr key={row.endpoint_id} className={TR}>
            <th scope="row" className={`${TD} max-w-0 font-normal`}>
              <Link
                href={agentHref(row.endpoint_id, {})}
                className="block truncate font-mono text-fg underline-offset-2 hover:underline"
                title="Logs deste agente"
              >
                {row.hostname ?? row.endpoint_id}
              </Link>
            </th>
            <td className={TD_NUM} title={formatInt(row.events_total)}>
              {formatCompact(row.events_total)}
            </td>
            <td className={`${TD} tnum whitespace-nowrap`}>{row.last_active_date ?? '—'}</td>
            <td className={TD}>
              <ShareBar value={row.events_total} max={max} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
